import { createSlice } from "@reduxjs/toolkit";

/**
 * Redux slice for managing insights dashboard state
 * @module insightsSlice
 */
const insightsSlice = createSlice({
  name: "insights",
  initialState: {
    tabs: [], // Available tabs from backend: [{key, label}, ...]
    activeTab: null, // Current active tab key
    data: {}, // Keyed by tab name: { overall: {...}, sales: {...}, ... }
    loading: false, // Initial fetch loading state
    loadingTabs: false, // Tabs configuration loading state
    error: null, // Error message
    refreshing: false, // Refresh loading state
    lastUpdated: null, // Last update timestamp
  },
  reducers: {
    /**
     * Set available tabs from backend
     * @param {Object} state
     * @param {Object} action - payload: { tabs: [{key, label}, ...], defaultTab: string }
     */
    setTabs: (state, action) => {
      const { tabs, defaultTab } = action.payload;
      state.tabs = tabs;
      state.activeTab = defaultTab || (tabs.length > 0 ? tabs[0].key : null);
      state.loadingTabs = false;
    },

    /**
     * Set tabs loading state
     * @param {Object} state
     * @param {Object} action - payload: boolean
     */
    setLoadingTabs: (state, action) => {
      state.loadingTabs = action.payload;
    },

    /**
     * Set the active tab
     * @param {Object} state
     * @param {Object} action - payload: tab name (string)
     */
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    /**
     * Set insights data for a specific tab
     * @param {Object} state
     * @param {Object} action - payload: { tab, data }
     */
    setInsightsData: (state, action) => {
      const { tab, data } = action.payload;
      state.data[tab] = data;
      state.lastUpdated = new Date().toISOString();
      state.loading = false;
      state.error = null;
    },

    /**
     * Set loading state
     * @param {Object} state
     * @param {Object} action - payload: boolean
     */
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    /**
     * Set error state
     * @param {Object} state
     * @param {Object} action - payload: error message (string)
     */
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.refreshing = false;
    },

    /**
     * Set refreshing state
     * @param {Object} state
     * @param {Object} action - payload: boolean
     */
    setRefreshing: (state, action) => {
      state.refreshing = action.payload;
    },

    /**
     * Clear error message
     * @param {Object} state
     */
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setTabs,
  setLoadingTabs,
  setActiveTab,
  setInsightsData,
  setLoading,
  setError,
  setRefreshing,
  clearError,
} = insightsSlice.actions;

export default insightsSlice.reducer;
